/**
 * Policy Engine — Load, validate, and expose the governance policy.
 *
 * ARCHITECTURE v4.0 — Phase 1, Step 1.3
 *
 * The Policy Engine is the single access point for all governance rules.
 * All validators query the policy engine instead of importing scattered constants.
 *
 * Design principle N7: Per-profile capable (future-proofs for audience profiles).
 */

import type { GovernancePolicy, VisualFamilyRule, SupportFamilyRule, QuestionTypeRule } from './policyTypes';
import type { GateThresholds } from './issueContract';
import type { ModuleId, QuestionType } from '../../content';
import { getTierConfig, type TierConfig } from '../../content';
import { DEFAULT_POLICY } from './defaultPolicy';
import { buildVisualPolicyFromMap } from './visualPolicyBuilder';
import { loadGovernanceConfig, getDisabledStages, type GovernanceConfigOverrides } from './governanceConfigStore';
import { error as logError } from '../../utils/logger';

// ─── Singleton Policy Instance ────────────────────────────────────────────────

let _activePolicy: GovernancePolicy = DEFAULT_POLICY;
let _initialized = false;
let _visualPolicyResolved = false;
let _storedConfig: GovernanceConfigOverrides | null = null;

/**
 * Ensure the visual policy is populated from seed data.
 * Called lazily to avoid circular dependency at module init time.
 */
function ensureVisualPolicy(): void {
  if (_visualPolicyResolved) return;
  try {
    const visual = buildVisualPolicyFromMap();
    _activePolicy = {
      ..._activePolicy,
      visual
    };
    _visualPolicyResolved = true;
  } catch {

    // If content isn't ready yet, skip — will retry on next access
  }
}

/**
 * Initialize the policy engine with a policy object.
 * Call once at startup. Defaults to DEFAULT_POLICY if not called.
 *
 * ENHANCED: Now also loads stored governance config overrides (CONFLICT-03 resolution).
 * ENHANCED: Lazily resolves visual policy from seeds to avoid circular init crash.
 */
export async function initializePolicy(policy?: GovernancePolicy): Promise<void> {
  _activePolicy = policy ?? DEFAULT_POLICY;
  _initialized = true;

  // Resolve visual policy from seeds (safe now — all modules loaded)
  ensureVisualPolicy();

  // Load stored config overrides
  _storedConfig = await loadGovernanceConfig();

  // Validate policy integrity
  const errors = validatePolicyIntegrity(_activePolicy);
  if (errors.length > 0) {
    logError('🚨 POLICY ENGINE: Integrity errors found:');
    errors.forEach(e => logError(`  ❌ ${e}`));
    if (process.env.NODE_ENV === 'development') {
      throw new Error(`Policy has ${errors.length} integrity error(s)`);
    }
  }
}

/**
 * Get the active governance policy.
 */
export function getPolicy(): GovernancePolicy {
  // Lazy-resolve visual policy on first access if not yet done
  if (!_visualPolicyResolved) {
    ensureVisualPolicy();
  }
  return _activePolicy;
}

/**
 * Check if the policy engine has been explicitly initialized.
 */
export function isPolicyInitialized(): boolean {
  return _initialized;
}

/**
 * Get the stored governance config overrides.
 * Returns null if no overrides are stored.
 */
export async function getStoredConfig(): Promise<GovernanceConfigOverrides | null> {
  if (!_storedConfig) {
    _storedConfig = await loadGovernanceConfig();
  }
  return _storedConfig;
}

/**
 * Refresh stored config from dataLayer.
 * Call after GovernanceSettingsTab saves new settings.
 */
export async function refreshStoredConfig(): Promise<void> {
  _storedConfig = await loadGovernanceConfig();
}

/**
 * Get pipeline stages that should be skipped based on stored config.
 */
export async function getSkipStages(): Promise<number[]> {
  const config = await getStoredConfig();
  if (!config) return [];
  return getDisabledStages(config);
}

// ─── Plus-Level Queries ───────────────────────────────────────────────────────

/**
 * Check if a tier is a plus-level tier.
 * Plus tiers demand higher cognitive level, more complex problems,
 * and transfer-oriented questions compared to their base counterpart.
 */
export function isPlusTier(tier: number): boolean {
  const config = getTierConfig(tier);
  return config?.isPlus ?? false;
}

/**
 * Get the base (non-plus) tier for a given plus tier.
 * E.g., M7+ (tier 10) → M7 (tier 9), B6+ (tier 2) → B6 (tier 1)
 * Returns the same tier if it's already a base tier.
 */
export function getBaseTierForPlus(tier: number): number {
  const config = getTierConfig(tier);
  if (!config || !config.isPlus) return tier;
  return tier - 1; // Plus tiers are always base + 1
}

/**
 * Get plus-level content requirements for a tier.
 * Returns null for non-plus tiers.
 *
 * Plus-level requirements (§3.3 Master Rulebook):
 * - minCognitiveLevel: Minimum cognitive level (application or analysis)
 * - minDidacticDepth: Minimum didactic depth (2=begrip or 3=transfer)
 * - minCitoDifficulty: Higher minimum citoDifficulty
 * - requireSolutionSteps: Whether solutionSteps are mandatory
 * - minSolutionSteps: Minimum number of solution steps
 * - requireMultiStep: Whether multi-step reasoning is expected
 */
export function getPlusRequirements(tier: number): {
  minCognitiveLevel: 'application' | 'analysis';
  minDidacticDepth: 2 | 3;
  minCitoDifficulty: number;
  requireSolutionSteps: boolean;
  minSolutionSteps: number;
  requireMultiStep: boolean;
} | null {
  if (!isPlusTier(tier)) return null;
  const band = getTierBand(tier);
  switch (band) {
    case 'basic':
      // B6+, M6+ (tiers 2, 4)
      return {
        minCognitiveLevel: 'application',
        minDidacticDepth: 2,
        minCitoDifficulty: 0.25,
        requireSolutionSteps: false,
        minSolutionSteps: 2,
        requireMultiStep: false
      };
    case 'intermediate':
      // E6+, B7+ (tiers 6, 8)
      return {
        minCognitiveLevel: 'application',
        minDidacticDepth: 2,
        minCitoDifficulty: 0.3,
        requireSolutionSteps: true,
        minSolutionSteps: 3,
        requireMultiStep: true
      };
    case 'advanced':
      // M7+, E7+ (tiers 10, 12)
      return {
        minCognitiveLevel: 'application',
        minDidacticDepth: 3,
        minCitoDifficulty: 0.35,
        requireSolutionSteps: true,
        minSolutionSteps: 3,
        requireMultiStep: true
      };
    case 'expert':
      // E8+ (tier 16)
      return {
        minCognitiveLevel: 'analysis',
        minDidacticDepth: 3,
        minCitoDifficulty: 0.4,
        requireSolutionSteps: true,
        minSolutionSteps: 4,
        requireMultiStep: true
      };
    default:
      return null;
  }
}

// ─── Policy Queries ───────────────────────────────────────────────────────────

/**
 * Get the tier band key for a tier number.
 * basic (1-4), intermediate (5-8), advanced (9-12), expert (13-16)
 */
export function getTierBand(tier: number): string {
  if (tier <= 4) return 'basic';
  if (tier <= 8) return 'intermediate';
  if (tier <= 12) return 'advanced';
  return 'expert';
}

/**
 * Get forbidden vocabulary for a tier.
 */
export function getForbiddenVocab(tier: number): string[] {
  const band = getTierBand(tier);
  return _activePolicy.vocab.forbiddenByTierBand[band] ?? [];
}

/**
 * Get maximum question text length for a tier.
 */
export function getMaxQuestionLength(tier: number, module?: ModuleId): number {
  const band = getTierBand(tier);

  // Check module override first
  if (module) {
    const moduleOverride = _activePolicy.contentBudgets.moduleOverrides?.[module];
    if (moduleOverride?.maxQuestionLengthByTierBand?.[band]) {
      return moduleOverride.maxQuestionLengthByTierBand[band];
    }
  }
  return _activePolicy.contentBudgets.maxQuestionLengthByTierBand[band] ?? 800;
}

/**
 * Get minimum explanation length for a tier.
 */
export function getMinExplanationLength(tier: number): number {
  const band = getTierBand(tier);
  return _activePolicy.contentBudgets.minExplanationLengthByTierBand[band] ?? 30;
}

/**
 * Get minimum distractor explanation length for a tier.
 */
export function getMinDistractorLength(tier: number): number {
  const band = getTierBand(tier);
  return _activePolicy.contentBudgets.minDistractorLengthByTierBand[band] ?? 15;
}

/**
 * Get maximum context field length for a tier (TAA/STU reading passages).
 */
export function getMaxContextLength(tier: number): number {
  const band = getTierBand(tier);
  return _activePolicy.contentBudgets.maxContextLengthByTierBand?.[band] ?? 2000;
}

/**
 * Get visual policy for a specific family.
 */
export function getVisualPolicy(familyId: string): VisualFamilyRule | null {
  return _activePolicy.visual.familyRules[familyId] ?? null;
}

/**
 * Check if a family requires a visual aid.
 */
export function familyRequiresVisual(familyId: string): boolean {
  return _activePolicy.visual.requiredFamilies.includes(familyId);
}

/**
 * Get support policy for a specific family.
 */
export function getSupportPolicy(familyId: string): SupportFamilyRule | null {
  return _activePolicy.support.familyRules[familyId] ?? null;
}

/**
 * Get question type validation rules.
 */
export function getQuestionTypeRule(type: QuestionType | string): QuestionTypeRule | null {
  return _activePolicy.questionType.typeRules[type] ?? null;
}

/**
 * Check if a question type is assessment-eligible.
 */
export function isAssessmentEligibleType(type: QuestionType | string): boolean {
  const rule = getQuestionTypeRule(type);
  return rule?.assessmentEligible ?? false;
}

/**
 * Get notation requirements for a module.
 */
export function getNotationRequirements(module: ModuleId): {
  requireLatexFractions: boolean;
  requireCommaDecimals: boolean;
  requireSuperscriptUnits: boolean;
  requireEuroComma: boolean;
} {
  return {
    requireLatexFractions: _activePolicy.notation.latexFractionModules.includes(module),
    requireCommaDecimals: _activePolicy.notation.commaDecimalModules.includes(module),
    requireSuperscriptUnits: _activePolicy.notation.superscriptUnitModules.includes(module),
    requireEuroComma: _activePolicy.notation.euroCommaModules.includes(module)
  };
}

/**
 * Get distractor misconception pattern for a module.
 */
export function getDistractorPattern(module: ModuleId): RegExp | null {
  const pattern = _activePolicy.distractor.modulePatterns[module];
  if (!pattern) return null;
  return new RegExp(pattern, 'i');
}

/**
 * Check if a module requires rule references in explanations.
 */
export function requiresRuleReference(module: ModuleId): boolean {
  return _activePolicy.explanation.moduleRuleRefRequired[module] ?? false;
}

/**
 * Get the minimum tier for check strategy requirement in a module.
 */
export function getCheckStrategyMinTier(module: ModuleId): number | null {
  return _activePolicy.explanation.moduleCheckStrategyMinTier[module] ?? null;
}

/**
 * Get gate thresholds.
 */
export function getGateThresholds(): GateThresholds {
  return _activePolicy.gates;
}

/**
 * Check if a governance policy feature flag is enabled.
 * NOTE: Renamed from isFeatureEnabled to avoid collision with config/featureFlags.ts (FF-3).
 */
export function isPolicyFeatureEnabled(flag: keyof GovernancePolicy['featureFlags']): boolean {
  return _activePolicy.featureFlags[flag] ?? false;
}

// ─── Policy Validation ────────────────────────────────────────────────────────

/**
 * Validate policy integrity.
 * Returns array of error strings (empty = valid).
 */
export function validatePolicyIntegrity(policy: GovernancePolicy): string[] {
  const errors: string[] = [];

  // Version check
  if (!policy.version || !/^\d+\.\d+\.\d+$/.test(policy.version)) {
    errors.push(`Invalid policy version: "${policy.version}" (expected semver)`);
  }

  // Gate thresholds sanity
  if (policy.gates.productionMinScore > policy.gates.assessmentMinScore) {
    errors.push('Production min score should not exceed assessment min score');
  }
  if (policy.gates.productionMinScore < 0 || policy.gates.productionMinScore > 100) {
    errors.push('Production min score must be 0-100');
  }
  if (policy.gates.assessmentMinScore < 0 || policy.gates.assessmentMinScore > 100) {
    errors.push('Assessment min score must be 0-100');
  }

  // Content budgets: min explanation should be less than max question length
  const tierBands = ['basic', 'intermediate', 'advanced', 'expert'];
  for (const band of tierBands) {
    const maxQ = policy.contentBudgets.maxQuestionLengthByTierBand[band];
    const minE = policy.contentBudgets.minExplanationLengthByTierBand[band];
    if (maxQ === undefined) {
      errors.push(`Missing maxQuestionLength for tier band "${band}"`);
    }
    if (minE === undefined) {
      errors.push(`Missing minExplanationLength for tier band "${band}"`);
    }
  }

  // Visual policy: required families should have rules
  for (const familyId of policy.visual.requiredFamilies) {
    if (!policy.visual.familyRules[familyId]) {
      errors.push(`Visual-required family "${familyId}" has no visual rules defined`);
    }
  }

  // At least one audience profile
  if (policy.audienceProfiles.length === 0) {
    errors.push('At least one audience profile is required');
  }
  return errors;
}